import * as React from "react";

export type Theme = "light" | "dark";

const THEME_KEY = "DRJOBS-THEME";
const DEFAULT_THEME: Theme = "light";

function readStoredTheme(): Theme {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch {
    // localStorage unavailable (Safari private mode)
  }
  return DEFAULT_THEME;
}

function applyThemeToDom(theme: Theme): void {
  if (theme === "dark") {
    document.documentElement.classList.add("dark");
  } else {
    document.documentElement.classList.remove("dark");
  }
}

function persistTheme(theme: Theme): void {
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {
    // silent — non-blocking
  }
}

// Runs synchronously at module load to prevent flash of wrong theme
let memoryTheme: Theme = readStoredTheme();
applyThemeToDom(memoryTheme);

const listeners = new Set<(theme: Theme) => void>();

function setTheme(theme: Theme): void {
  memoryTheme = theme;
  applyThemeToDom(theme);
  persistTheme(theme);
  listeners.forEach((l) => l(theme));
}

export function useTheme(): { theme: Theme; setTheme: (theme: Theme) => void } {
  const [theme, setLocalTheme] = React.useState<Theme>(memoryTheme);

  React.useEffect(() => {
    listeners.add(setLocalTheme);
    return () => {
      listeners.delete(setLocalTheme);
    };
  }, []);

  return { theme, setTheme };
}
